import { z } from 'zod';

// Base URL of the API that serves the OAuth 2.1 authorization endpoints. In
// production the API is served from the same origin as the app, so this is left
// empty and we fall back to `window.location.origin`. In local development the
// API runs on a different host/port, so set VITE_OAUTH_API_BASE_URL accordingly
// (e.g. http://localhost:4000), or rely on the `/oauth` Vite dev proxy.
const OAuthApiBaseUrl = import.meta.env.VITE_OAUTH_API_BASE_URL ?? '';

const OAuthSessionParam = 'oauth_session';

const oauthApiUrl = (path: string): URL => {
  const base = OAuthApiBaseUrl === '' ? window.location.origin : OAuthApiBaseUrl;
  return new URL(path, base);
};

export const getOAuthSession = (search: string): string | null =>
  new URLSearchParams(search).get(OAuthSessionParam);

// The user's Allow/Deny decision. On allow we hand the API a fresh Firebase ID
// token and the chosen account; on deny we send neither, so no token can be
// issued. `oauth_session` is carried through unchanged as an opaque token.
export type OAuthCallbackDecision =
  | { decision: 'allow'; oauthSession: string; idToken: string; accountId: string }
  | { decision: 'deny'; oauthSession: string };

// Builds the absolute URL of OUR OWN OAuth callback. The callback path is fixed
// and never derived from the query string, so this can't be turned into an open
// redirect.
export const buildOAuthCallbackUrl = (params: OAuthCallbackDecision): string => {
  const url = oauthApiUrl('/oauth/authorize/callback');
  url.searchParams.set('oauth_session', params.oauthSession);
  url.searchParams.set('decision', params.decision);
  if (params.decision === 'allow') {
    url.searchParams.set('id_token', params.idToken);
    url.searchParams.set('account_id', params.accountId);
  }
  return url.toString();
};

// The client details and requested scopes shown on the consent screen. Note that
// clientName / logoUri / clientUri are self-asserted by a dynamically registered
// client and are NOT verified — only redirectHost is trustworthy.
export const oauthConsentSchema = z.object({
  clientId: z.string(),
  // True when the client's redirect host matches the known-client registry. When
  // verified, clientName/logoUri are the curated values; otherwise self-asserted.
  verified: z.boolean(),
  clientName: z.string(),
  logoUri: z.string().nullable(),
  clientUri: z.string().nullable(),
  scopes: z.array(z.string()),
  redirectUri: z.string(),
  redirectHost: z.string(),
});

export type OAuthConsent = z.infer<typeof oauthConsentSchema>;

export type OAuthConsentResult =
  | { status: 'ok'; consent: OAuthConsent }
  | { status: 'expired' }
  | { status: 'error' };

export const fetchOAuthConsent = async (oauthSession: string): Promise<OAuthConsentResult> => {
  try {
    const url = oauthApiUrl('/oauth/authorize/consent');
    url.searchParams.set('oauth_session', oauthSession);

    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });

    // An invalid/expired session returns 400 invalid_request.
    if (response.status === 400) {
      return { status: 'expired' };
    }
    if (!response.ok) {
      return { status: 'error' };
    }

    const parsed = oauthConsentSchema.safeParse(await response.json());
    return parsed.success ? { status: 'ok', consent: parsed.data } : { status: 'error' };
  } catch {
    return { status: 'error' };
  }
};

const ScopeDescriptions: Record<string, string> = {
  'data:read': 'Read your data and run queries',
};

// Human-readable description for a requested scope. Unknown scopes are rendered
// verbatim so the user still sees exactly what is being requested.
export const describeScope = (scope: string): string => ScopeDescriptions[scope] ?? scope;
